
import React, { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import EditorToolbar from './editor/EditorToolbar';
import EditorContent from './editor/EditorContent';
import Menu from './Menu';
import LinkDialog from './editor/LinkDialog';
import CommentDialog from './editor/CommentDialog';
import MeetingTranscriptionDialog from './editor/MeetingTranscriptionDialog';
import SearchAndReplace from './editor/SearchAndReplace';
import EditorActions from './editor/EditorActions';
import { exportToPDF, exportToExcel, exportToDoc, exportToMarkdown } from '@/utils/exportUtils';
import { useNoteState } from './editor/NoteState';
import { useSaveHandler } from './editor/SaveHandler';
import { useCommentHandler } from './editor/CommentHandler';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Group, NetworkProps } from '@/types/note';
import { useTheme } from "next-themes";
import { getThemeColor } from "@/utils/colorUtils";

import { resolveEmbedUrl } from './editor/mediaEmbedUpgrader';

const getDirectMediaKind = (url: string) => {
  const cleanUrl = url.trim().split(/[?#]/)[0].toLowerCase();
  if (/^data:video\//i.test(url) || /\.(mp4|m4v|mov|webm|ogv|m3u8)$/i.test(cleanUrl)) return 'video';
  if (/^data:audio\//i.test(url) || /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac|weba)$/i.test(cleanUrl)) return 'audio';
  return null;
};

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const createLinkOrMediaMarkdown = (url: string, label: string) => {
  const direct = getDirectMediaKind(url);
  if (direct === 'video') return `<video src="${escapeAttribute(url)}" data-name="${escapeAttribute(label)}" controls></video>`;
  if (direct === 'audio') return `<audio src="${escapeAttribute(url)}" controls></audio>`;
  // Known embed-provider URL (YouTube, Vimeo, SoundCloud, Spotify…): insert
  // as a media tag so the embed upgrader swaps it for the proper iframe.
  const embed = resolveEmbedUrl(url);
  if (embed?.kind === 'video') return `<video src="${escapeAttribute(url)}" data-name="${escapeAttribute(label)}" controls></video>`;
  if (embed?.kind === 'audio') return `<audio src="${escapeAttribute(url)}" controls></audio>`;
  return `[${label}](${url})`;
};

const NoteEditor: React.FC<NetworkProps> = ({ isOnline }) => {
  const location = useLocation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const {
    title, setTitle,
    content, setContent,
    restoreContent,
    resetContent,
    color, setColor,
    showLinkDialog, setShowLinkDialog,
    linkUrl, setLinkUrl,
    coverImage, setCoverImage,
    selectedGroup, setSelectedGroup,
    textHistory,
    restoreRevision,
    showRecoveryDialog,
    setShowRecoveryDialog,
  } = useNoteState();
  
  const [showMeetingDialog, setShowMeetingDialog] = React.useState(false);
  const [showSearchDialog, setShowSearchDialog] = React.useState(false);
  // HTML view removed — WYSIWYG (BlockNote) is now the only editing mode.
  const isWysiwygMode = true;
  const setIsWysiwygMode = (_: boolean) => {};
  const [selectedCommentId, setSelectedCommentId] = React.useState<string | null>(null);
  
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [noteId, setNoteId] = React.useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const latestContentRef = useRef(content);
  const transcriptInsertInFlightRef = useRef<Set<string>>(new Set());
  const [currentElement, setCurrentElement] = React.useState<HTMLTextAreaElement | HTMLDivElement | null>(null);
  const contentEditableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    latestContentRef.current = content;
  }, [content]);

  const normalizeTranscriptForDedup = (value: string) => value
    .replace(/<[^>]*>/g, ' ')
    .replace(/^\s*#{1,6}\s*/gm, '')
    .replace(/\*\*/g, '')
    .replace(/\bmeeting transcript\b/gi, '')
    .replace(/<!--\s*mindnote-transcript:[^>]*-->/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const appendTranscriptOnce = async (text: string, transcriptId?: string) => {
    const transcript = text.trim();
    const transcriptKey = transcriptId ? `id:${transcriptId}` : normalizeTranscriptForDedup(transcript);
    if (!transcript || !transcriptKey || transcriptInsertInFlightRef.current.has(transcriptKey)) return false;

    transcriptInsertInFlightRef.current.add(transcriptKey);
    try {
      const transcriptBlock = transcriptId ? `<!-- mindnote-transcript:${transcriptId} -->\n${transcript}` : transcript;
      const appendToBlockNoteEnd = window.appendToBlockNoteEnd;
      if (typeof appendToBlockNoteEnd === 'function') {
        const nextBlockNoteContent = await appendToBlockNoteEnd(transcriptBlock);
        if (nextBlockNoteContent) {
          latestContentRef.current = nextBlockNoteContent;
          return true;
        }
      }

      const freshBlockNoteContent = typeof window.getBlockNoteMarkdown === 'function'
        ? await window.getBlockNoteMarkdown().catch(() => undefined)
        : undefined;
      const blockNoteContent = freshBlockNoteContent ?? window.__mindnoteLatestBlockNoteMarkdown;
      const currentContent = typeof blockNoteContent === 'string' ? blockNoteContent : latestContentRef.current;
      const nextContent = `${currentContent.trimEnd()}\n\n${transcriptBlock}`.trimStart();
      latestContentRef.current = nextContent;
      window.__mindnoteLatestBlockNoteMarkdown = nextContent;
      setContent(nextContent);
      return true;
    } finally {
      window.setTimeout(() => {
        transcriptInsertInFlightRef.current.delete(transcriptKey);
      }, 500);
    }
  };
  const getExportContent = () => {
    // Always export the WYSIWYG-visible text (no HTML tags),
    // regardless of the currently selected mode
    if (currentElement && currentElement instanceof HTMLDivElement) {
      return currentElement.innerText;
    }
    // Fallback: strip tags from the stored content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    return tempDiv.innerText;
  };

  const exportActions = {
    onExportPDF: () => exportToPDF(getExportContent()),
    onExportExcel: () => exportToExcel(getExportContent()),
    onExportDoc: () => exportToDoc(getExportContent()),
    onExportMarkdown: () => exportToMarkdown(title, content) // Always export original markdown content
  };

  const commentHandler = useCommentHandler();
  const {
    comments,
    selectedText,
    showCommentDialog,
    showSelectionError,
    setShowCommentDialog,
    handleTextSelection,
    handleAddComment,
    handleDeleteComment,
    handleCommentButtonClick
  } = commentHandler;

  const handleEditorElementRef = React.useCallback((el: HTMLTextAreaElement | HTMLDivElement | null) => {
    setCurrentElement((current) => (current === el ? current : el));
    (contentEditableRef as React.MutableRefObject<HTMLDivElement | null>).current = el instanceof HTMLDivElement ? el : null;
  }, []);

  const handleEditorTextSelection = React.useCallback(() => {
    handleTextSelection(currentElement || textareaRef, latestContentRef.current, isWysiwygMode);
  }, [handleTextSelection, currentElement, textareaRef, isWysiwygMode]);

  // Preserve selection when clicking toolbar buttons
  const handleToolbarInteraction = (callback: () => void) => {
    const element = currentElement;
    if (element) {
      let start: number, end: number;
      
      if (element instanceof HTMLTextAreaElement) {
        start = element.selectionStart;
        end = element.selectionEnd;
      } else if (element instanceof HTMLDivElement) {
        start = (element as any).selectionStart || 0;
        end = (element as any).selectionEnd || 0;
      } else {
        start = 0;
        end = 0;
      }
      
      // Execute the callback
      callback();
      
      // Restore selection after a brief delay (mainly for textarea)
      setTimeout(() => {
        if (element instanceof HTMLTextAreaElement && start !== end) {
          element.focus();
          element.setSelectionRange(start, end);
        }
      }, 50);
    } else {
      callback();
    }
  };

  const { handleSave, isSaving } = useSaveHandler({
    title,
    content,
    color,
    coverImage,
    selectedGroup,
    noteId,
    comments,
  });

  const handleCopy = async () => {
    try {
      let textToCopy = content;

      if (isWysiwygMode) {
        // In WYSIWYG mode, copy the visible text without HTML tags
        if (currentElement && currentElement instanceof HTMLDivElement) {
          textToCopy = currentElement.innerText;
        } else {
          // Fallback: strip tags from content string
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = content;
          textToCopy = tempDiv.innerText;
        }
      }

      await navigator.clipboard.writeText(textToCopy);
      toast({
        title: "Copied",
        description: "Content copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Failed to copy content to clipboard",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const fetchGroups = async () => {
      const { data: fetchedGroups, error } = await supabase
        .from('groups')
        .select('*');

      if (!error && fetchedGroups) {
        setGroups(fetchedGroups);
      }
    };

    fetchGroups();

    const channel = supabase
      .channel('public:groups')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'groups' },
        () => {
          fetchGroups();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    const state = location.state as { note?: { id?: string; title: string; content: string; color: string; cover_image?: string; group_id?: string } };
    if (state?.note) {
      setNoteId(state.note.id || null);
      setTitle(state.note.title);
      resetContent(state.note.content);
      setColor(state.note.color);
      setCoverImage(state.note.cover_image || null);
      setSelectedGroup(state.note.group_id || "");
    }
  }, [location]);

  // Load persisted comments when a note is opened
  useEffect(() => {
    if (!noteId) return;
    try {
      const key = `note_comments_${noteId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) commentHandler.setComments(parsed);
      }
    } catch (e) {
      console.warn('Failed to load comments from storage', e);
    }
  }, [noteId]);

  // Persist comments whenever they change
  useEffect(() => {
    if (!noteId) return;
    try {
      localStorage.setItem(`note_comments_${noteId}`, JSON.stringify(comments));
    } catch (e) {
      console.warn('Failed to save comments to storage', e);
    }
  }, [comments, noteId]);

  useEffect(() => {
    const w = window as any;
    w.openCommentForText = (start: number, end: number, commentId: string) => {
      setSelectedCommentId(commentId);
      setShowCommentDialog(true);
    };
    
    // Set up text selection function for CommentDialog to use
    w.selectTextInEditor = (start: number, end: number) => {
      if (isWysiwygMode) {
        // For WYSIWYG mode, call the selectCommentText function that exists in NoteContent
        if (w.selectCommentText) {
          w.selectCommentText(start, end);
        }
      } else {
        // For HTML mode (textarea), directly select the text
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(start, end);
        }
      }
    };
  }, [setShowCommentDialog, isWysiwygMode, textareaRef]);

  return (
    <div className="relative min-h-screen pb-20">
      {/* Selection Error Message */}
      {showSelectionError && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse">
          Please select text first, then click the comment icon to add your comment
        </div>
      )}
      
      <div id="note-content" className="space-y-4 p-4 bg-white rounded-lg shadow-sm max-w-4xl mx-auto" style={{ backgroundColor: getThemeColor(color, theme === 'dark') }}>
        <EditorContent
          title={title}
          content={content}
          externalRevision={restoreRevision}
          color={color}
          coverImage={coverImage}
          setTitle={setTitle}
          setContent={setContent}
          setCoverImage={setCoverImage}
          textareaRef={textareaRef}
          handleTextSelection={handleEditorTextSelection}
          comments={comments}
          isWysiwygMode={isWysiwygMode}
          onGetCurrentElement={handleEditorElementRef}
          onOpenCommentForText={(start, end, commentId) => {
            setSelectedCommentId(commentId);
            setShowCommentDialog(true);
          }}
        />

        <EditorActions
          selectedGroup={selectedGroup}
          groups={groups}
          onGroupChange={setSelectedGroup}
          onSave={handleSave}
          isSaving={isSaving}
        />

        <EditorToolbar
          onImageUpload={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onloadend = () => {
                const imageUrl = reader.result as string;
                setContent(content + `\n![${file.name}](${imageUrl})`);
              };
              reader.readAsDataURL(file);
            }
          }}
          onLinkClick={() => setShowLinkDialog(true)}
          onColorSelect={setColor}
          selectedColor={color}
          content={content}
          onContentUpdate={setContent}
          onCopy={handleCopy}
          {...exportActions}
          onCommentClick={() => handleToolbarInteraction(() => handleCommentButtonClick(currentElement, isWysiwygMode))}
          onMeetingTranscriptionClick={() => setShowMeetingDialog(true)}
          onSearchClick={() => setShowSearchDialog(true)}
          textHistory={textHistory}
          showRecoveryDialog={showRecoveryDialog}
          setShowRecoveryDialog={setShowRecoveryDialog}
          isWysiwygMode={isWysiwygMode}
          onToggleWysiwyg={() => setIsWysiwygMode(!isWysiwygMode)}
          selectedText={selectedText}
          contentEditableRef={contentEditableRef}
          noteId={noteId}
        />

        <LinkDialog
          showDialog={showLinkDialog}
          onOpenChange={setShowLinkDialog}
          linkUrl={linkUrl}
          onLinkUrlChange={setLinkUrl}
          selectedText={selectedText?.text}
          onAddLink={async (url, displayText, useAsAIContext) => {
            const label = (displayText && displayText.length > 0) ? displayText : url;
            const markdown = createLinkOrMediaMarkdown(url, label);

            let inserted = false;
            // Always insert at the BlockNote caret. We intentionally do NOT
            // replace any editor selection here — the user names the link via
            // the dialog's Display text field.
            const bnInsert = (window as any).insertAtBlockNoteCaret as
              | ((md: string) => Promise<boolean>)
              | undefined;
            if (typeof bnInsert === 'function') {
              try { inserted = await bnInsert(markdown); } catch { inserted = false; }
            }
            if (!inserted) {
              setContent(content ? `${content} ${markdown}` : markdown);
            }


            if (useAsAIContext) {
              try {
                const key = `note_ai_context_urls_${noteId ?? 'new'}`;
                const existing = JSON.parse(localStorage.getItem(key) ?? '[]');
                const list = Array.isArray(existing) ? existing : [];
                if (!list.includes(url)) {
                  list.push(url);
                  localStorage.setItem(key, JSON.stringify(list));
                }
                toast({
                  title: "Added to AI context",
                  description: "The AI will consider this link when answering in this note.",
                });
              } catch (e) {
                console.warn('Failed to persist AI-context link', e);
              }
            }

            setLinkUrl("");
            setShowLinkDialog(false);
          }}

        />

        <CommentDialog
          showDialog={showCommentDialog}
          onOpenChange={(open) => {
            setShowCommentDialog(open);
            if (!open) setSelectedCommentId(null);
          }}
          selectedText={selectedText}
          comments={comments}
          selectedCommentId={selectedCommentId}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          onSelectTextInEditor={(start, end) => {
            // Close dialog to avoid focus trap, then select text in editor
            setShowCommentDialog(false);
            setTimeout(() => {
              const w = window as any;
              if (w && typeof w.selectCommentText === 'function') {
                w.selectCommentText(start, end);
              } else if (textareaRef?.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(start, end);
              }
            }, 30);
          }}
        />

        <MeetingTranscriptionDialog
          showDialog={showMeetingDialog}
          onOpenChange={setShowMeetingDialog}
          noteId={noteId}
          onTranscriptionComplete={appendTranscriptOnce}
        />

        <SearchAndReplace
          content={content}
          onContentChange={setContent}
          isVisible={showSearchDialog}
          onClose={() => setShowSearchDialog(false)}
          textareaRef={textareaRef}
          contentEditableRef={contentEditableRef}
          isWysiwygMode={isWysiwygMode}
        />
      </div>
      <Menu />
    </div>
  );
};

export default NoteEditor;
